import React, { useState, useEffect, useCallback, Suspense } from 'react';
import { Helmet } from 'react-helmet';
import { Container, Row, Col, Button, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import useUser from '../../hooks/useUser';
import useCheckoutRedirect from '../../hooks/useCheckoutRedirect';
import useCourseData from '../../hooks/useCourseData';
import { pushEvent, ANALYTICS_EVENTS } from '../../utils/analytics';
import { getApiBaseUrl } from '../../utils/apiBaseUrl';
import './AIBootcampSalesPage.css';
import BootcampHero from '../../components/BootcampHero';
import BootcampOverview from '../../components/BootcampOverview';
import BootcampBenefits from '../../components/BootcampBenefits';
import BootcampCurriculum from '../../components/BootcampCurriculum';
import FAQSection from '../../components/FAQSection';
import BootcampPricingSection from '../../components/BootcampPricingSection';
import {
  FaRocket,
  FaStar,
  FaGraduationCap,
  FaRobot,
  FaChartLine,
  FaUsers
} from 'react-icons/fa';

// Lazy load TestimonialSlider for performance
const TestimonialSlider = React.lazy(() => import('../../components/TestimonialSlider'));

const BOOTCAMP_FIT_CALL_URL = 'https://calendly.com/lumiwealth/botspot-call';
const SELF_PACED_BOOTCAMP_PATH = '/ai-bot-builder-bootcamp/self-paced';
const LIVE_BOOTCAMP_FALLBACK_PAYMENT_PLANS = [
  {
    planKey: 'bootcamp-5mo',
    label: '5 Monthly Payments',
    amount: 69900,
    originalAmount: 69900,
    currency: 'usd',
    interval: 'month',
    intervalCount: 1,
    metadata: { installments: 5 },
    installments: 5,
    totalAmount: 349500,
    originalTotalAmount: 349500,
    hasDiscount: false,
    appliedPromotion: null,
  },
  {
    planKey: 'bootcamp-3mo',
    label: '3 Monthly Payments',
    amount: 109900,
    originalAmount: 109900,
    currency: 'usd',
    interval: 'month',
    intervalCount: 1,
    metadata: { installments: 3 },
    installments: 3,
    totalAmount: 329700,
    originalTotalAmount: 329700,
    hasDiscount: false,
    appliedPromotion: null,
  },
  {
    planKey: 'bootcamp-upfront',
    label: 'Pay in Full',
    amount: 299900,
    originalAmount: 299900,
    currency: 'usd',
    interval: null,
    intervalCount: null,
    metadata: { installments: 1 },
    installments: 1,
    totalAmount: 299900,
    originalTotalAmount: 299900,
    hasDiscount: false,
    appliedPromotion: null,
  },
];

// Bootcamp testimonials data (ordered longest to shortest)
const bootcampTestimonials = [
  {
    quote: "I have decades of experience in investing, option trading and software. I still find the Lumiwealth Pro Plan of great value.",
    author: "Ali",
    role: "Trustpilot Reviewer, 2024"
  },
  {
    quote: "I really like Robert's way of doing things, he is so calm and very knowledgeable about financial markets.",
    //author: "Daniel Ogola",
    author: "Daniel O.",
    role: "Trustpilot Reviewer, 2024"
  },
  {
    quote: "The step-by-step process was very easy to follow. It took all of the guesswork out of the process.",
    //author: "Robert Sanon",
    author: "Robert S.",
    role: "Trustpilot Reviewer, 2024"
  },
  {
    quote: "Robert and Lumiwealth have been instrumental in the acceleration of my trading journey.",
    author: "Worth",
    role: "Trustpilot Reviewer, 2024"
  },
  {
    quote: "Even though I had to miss the classes, the videos of the classes were very helpful.",
    //author: "Robert E Dorans",
    author: "Robert D.",
    role: "Trustpilot Reviewer, 2023"
  },
  {
    quote: "Rob was a great teacher, very informative, always available for tutoring meetings.",
    author: "Anthony",
    role: "Trustpilot Reviewer, 2023"
  },
  {
    quote: "Rob and his team have made algo trading accessible to all. Kudos to the team.",
    //author: "Rajesh Bollapragada",
    author: "Rajesh B.",
    role: "Trustpilot Reviewer, 2023"
  },
  {
    quote: "I learned a lot from Rob's classes, they are so excellent and practical.",
    //author: "Mohsen Selseleh",
    author: "Mohsen S.",
    role: "Trustpilot Reviewer, 2023"
  },
  {
    quote: "Outstanding course, Rob and his team are amazing teachers/trainers.",
    //author: "Dea Elmi",
    author: "Dea E.",
    role: "Trustpilot Reviewer, 2023"
  },
  {
    quote: "I went from zero Python to algorithm in what I feel is record time!",
    //author: "Robert MacLeod",
    author: "Robert M.",
    role: "Trustpilot Reviewer, 2023"
  }
];

const AIBootcampSalesPage = () => {
  const { user } = useUser();
  const [scrollProgress, setScrollProgress] = useState(0);

  const { startCheckout: startBootcampCheckout } = useCheckoutRedirect('ai_bootcamp_sales');

  // Add course data hook for bootcamp components
  const { bootcampData, isLoading: isCourseLoading, nextSessionDate, getWeekDate } = useCourseData(true);
  const liveBootcampSessionDate = nextSessionDate?.rawDate ? nextSessionDate : null;

  // Add pricing state for proper price display
  const [pricingInfo, setPricingInfo] = useState(null);
  const [isLoadingPricing, setIsLoadingPricing] = useState(true);
  const [pricingError, setPricingError] = useState(null);

  // Add state for sale end time from pricing API
  const [saleEndTime, setSaleEndTime] = useState(null);

  // Add state for payment plans from catalog
  // Default to 5 monthly payments (most popular and accessible option)
  const [paymentPlans, setPaymentPlans] = useState([]);
  const [selectedPlanKey, setSelectedPlanKey] = useState('bootcamp-5mo');

  const trackBootcampChoice = useCallback(
    ({ path, ctaLocation, productSlug, planKey, destination }) => {
      pushEvent({
        event: ANALYTICS_EVENTS.BOOTCAMP_PATH_SELECTED,
        source: window.location.pathname,
        path,
        cta_location: ctaLocation,
        productSlug,
        planKey,
        destination,
        userId: user?.id || user?.sub || user?.userId,
        email: user?.email,
      });
    },
    [user?.email, user?.id, user?.sub, user?.userId],
  );

  useEffect(() => {
    // Scroll progress tracking
    const handleScroll = () => {
      const winScroll = document.body.scrollTop || document.documentElement.scrollTop;
      const height = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const scrolled = (winScroll / height) * 100;
      setScrollProgress(scrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const beginBootcampCheckout = useCallback(
    (ctaLocation = 'bootcamp_primary_cta') => {
      trackBootcampChoice({
        path: 'live',
        ctaLocation,
        productSlug: 'ai-bootcamp',
        planKey: selectedPlanKey,
        destination: '/checkout',
      });

      pushEvent({
        event: ANALYTICS_EVENTS.BOOTCAMP_CHECKOUT_STARTED,
        source: window.location.pathname,
        path: 'live',
        cta_location: ctaLocation,
        productSlug: 'ai-bootcamp',
        planKey: selectedPlanKey,
        userId: user?.id || user?.sub || user?.userId,
        email: user?.email,
      });

      return startBootcampCheckout({
        products: [{ productSlug: 'ai-bootcamp', planKey: selectedPlanKey }],
        flowContext: 'ai_bootcamp_sales',
        metadata: {
          origin: {
            flow: 'ai_bootcamp_sales',
            ctaLocation,
            selectedPlan: selectedPlanKey,
            returnUrl: '/ai-bot-builder-bootcamp/watch',
          },
        },
        contact: {
          email: user?.email || '',
          name: user?.name || '',
        },
      });
    },
    [startBootcampCheckout, trackBootcampChoice, user?.email, user?.id, user?.name, user?.sub, user?.userId, selectedPlanKey],
  );

  // Add helper functions for pricing transparency
  const addDollarSign = (str) => {
    if (!str || str === 'TBD') return str;
    const withDollar = str.startsWith('$') ? str : `$${str}`;
    return withDollar.replace('/month', '/mo');
  };

  const getProductDisplayDetails = (slug) => {
    if (!pricingInfo?.products) {
      return {
        currentFormatted: 'TBD',
        originalFormatted: 'TBD',
        isDiscounted: false,
        discountPercent: 0,
        totalChargeAmount: 0
      };
    }

    const product = pricingInfo.products.find(p => p.slug === slug);
    if (!product) {
      return {
        currentFormatted: 'TBD',
        originalFormatted: 'TBD',
        isDiscounted: false,
        discountPercent: 0,
        totalChargeAmount: 0
      };
    }

    const currentPrice = product.discountedPrice || product.basePrice;
    const originalPrice = product.basePrice;
    const isDiscounted = !!product.discountedPrice;
    const discountPercent = isDiscounted ? Math.round(((originalPrice.amount - currentPrice.amount) / originalPrice.amount) * 100) : 0;

    return {
      currentFormatted: addDollarSign(currentPrice.formatted),
      originalFormatted: addDollarSign(originalPrice.formatted),
      isDiscounted,
      discountPercent,
      totalChargeAmount: currentPrice.amount / 100 // Convert cents to dollars
    };
  };

  useEffect(() => {
    // Comprehensive pricing fetch (matches UpgradeToPro logic)
    const fetchPricing = async () => {
      setIsLoadingPricing(true);
      setPricingError(null);

      try {
        // Use same API pattern as UpgradeToProPage with fallback
        const API_URL = getApiBaseUrl() || 'https://api.botspot.trade';
        // Fetch AI Bootcamp pricing (not pro-plan - they are separate products)
        const response = await fetch(`${API_URL}/stripe/products-pricing?slugs=ai-bootcamp`);
        if (!response.ok) {
          throw new Error(`HTTP error ${response.status}`);
        }
        const data = await response.json();
        console.log('Pricing data received:', data); // Debug log
        setPricingInfo(data);

        // Extract sale end time from activePromotion if available
        let endDate = null;
        if (data.activePromotion && data.activePromotion.endDate) {
          console.log('Active promotion end date:', data.activePromotion.endDate);
          const promotionDate = new Date(data.activePromotion.endDate);
          const now = new Date();

          console.log('Promotion date:', promotionDate.toString());
          console.log('Current date:', now.toString());
          console.log('Is promotion date in future?', promotionDate > now);

          // Only show countdown if promotion is genuinely in the future
          if (promotionDate > now && !isNaN(promotionDate.getTime())) {
            endDate = promotionDate;
            console.log('Using backend promotion end date:', promotionDate.toISOString());
          } else {
            console.log('Promotion is expired or invalid date, hiding countdown');
            endDate = null;
          }
        } else {
          console.log('No active promotion found, hiding countdown timer');
          endDate = null;
        }

        // Ensure the date is valid
        if (endDate && !isNaN(endDate.getTime())) {
          setSaleEndTime(endDate);
          console.log('Sale end time set to:', endDate);
        } else {
          if (endDate) {
            console.error('Invalid end date:', endDate);
          }
          setSaleEndTime(null);
        }
      } catch (error) {
        console.error('Error fetching pricing:', error);
        setPricingError(null);
        // Set fallback pricing to prevent UI breaking. Checkout still resolves
        // final prices server-side.
        const fallbackData = {
          products: [
            { slug: 'ai-bootcamp', basePrice: { formatted: '$2,999', amount: 299900 } }
          ]
        };
        setPricingInfo(fallbackData);

        // On API error, don't show countdown since we can't verify real promotions
        console.log('API error - hiding countdown timer');
        setSaleEndTime(null);
      } finally {
        setIsLoadingPricing(false);
      }
    };

    // Fetch payment plans from catalog API
    const fetchPaymentPlans = async () => {
      try {
        const API_URL = getApiBaseUrl() || 'https://api.botspot.trade';
        const response = await fetch(`${API_URL}/checkout/catalog`);
        if (response.ok) {
          const data = await response.json();
          // Find ai-bootcamp product and extract its plans
          const bootcampProduct = data.products?.find(p => p.slug === 'ai-bootcamp');
          if (bootcampProduct?.plans?.length > 0) {
            // Sort plans by displayOrder (via metadata or array order) and filter active ones
            const plans = bootcampProduct.plans
              .filter(plan => plan.stripePriceId) // Ensure plan has valid price
              .map(plan => {
                // Use discountedAmount if available (promotion applied), otherwise use original amount
                const effectiveAmount = plan.discountedAmount ?? plan.amount;
                const originalAmount = plan.amount;
                const hasDiscount = plan.discountedAmount !== null && plan.discountedAmount !== undefined && plan.discountedAmount !== plan.amount;
                const installments = plan.metadata?.installments || 1;

                return {
                  planKey: plan.planKey,
                  label: plan.label,
                  amount: effectiveAmount, // Discounted price in cents (for display)
                  originalAmount: originalAmount, // Original price in cents (for strikethrough)
                  currency: plan.currency || 'usd',
                  interval: plan.interval,
                  intervalCount: plan.intervalCount,
                  isDefault: plan.default,
                  metadata: plan.metadata,
                  // Promotion info
                  hasDiscount,
                  appliedPromotion: plan.appliedPromotion || null,
                  // Calculate display info
                  installments,
                  totalAmount: effectiveAmount * installments,
                  originalTotalAmount: originalAmount * installments
                };
              });
            // Sort plans by installments in DESCENDING order (5 monthly first, then 3, then upfront)
            // This puts the most accessible option (5 monthly payments) on the left
            const sortedPlans = plans.sort((a, b) => {
              const aInstallments = a.installments || a.metadata?.installments || 1;
              const bInstallments = b.installments || b.metadata?.installments || 1;
              return bInstallments - aInstallments; // Descending order
            });
            console.log('Payment plans loaded (sorted):', sortedPlans);
            setPaymentPlans(sortedPlans);

            // IMPORTANT: Default to 5 monthly payments (bootcamp-5mo) - the most accessible option
            // Do NOT use API's isDefault field - we want 5 monthly as default for better conversion
            const fiveMonthPlan = sortedPlans.find(p => p.planKey === 'bootcamp-5mo');
            if (fiveMonthPlan) {
              setSelectedPlanKey('bootcamp-5mo');
            }

            // Also use activePromotion from catalog for sale end time if not already set
            if (data.activePromotion?.endDate && !saleEndTime) {
              const catalogPromotionDate = new Date(data.activePromotion.endDate);
              const now = new Date();
              if (catalogPromotionDate > now && !isNaN(catalogPromotionDate.getTime())) {
                console.log('Using catalog promotion end date for countdown:', catalogPromotionDate.toISOString());
                setSaleEndTime(catalogPromotionDate);
              }
            }
          } else {
            setPaymentPlans(LIVE_BOOTCAMP_FALLBACK_PAYMENT_PLANS);
            setSelectedPlanKey('bootcamp-5mo');
          }
        } else {
          setPaymentPlans(LIVE_BOOTCAMP_FALLBACK_PAYMENT_PLANS);
          setSelectedPlanKey('bootcamp-5mo');
        }
      } catch (error) {
        console.error('Error fetching payment plans:', error);
        setPaymentPlans(LIVE_BOOTCAMP_FALLBACK_PAYMENT_PLANS);
        setSelectedPlanKey('bootcamp-5mo');
      }
    };

    // Fetch bootcamp course data for better context
    const fetchCourseData = async () => {
      try {
        const courseId = process.env.REACT_APP_AI_BOT_BUILDER_BOOTCAMP_COURSE_ID;
        if (courseId) {
          // Use same API pattern as UpgradeToProPage with fallback
          const API_URL = getApiBaseUrl() || 'https://api.botspot.trade';
          const response = await fetch(`${API_URL}/courses/${courseId}`);
          if (response.ok) {
            await response.json();
          }
        }
      } catch (error) {
        console.error('Error fetching course data:', error);
      }
    };

    fetchPricing();
    fetchPaymentPlans();
    fetchCourseData();
  }, []);

  const handleProCtaClick = () => {
    beginBootcampCheckout('bootcamp_secondary_cta');
  };

  const handlePlanSelect = useCallback((planKey) => {
    pushEvent({
      event: ANALYTICS_EVENTS.BOOTCAMP_PLAN_SELECTED,
      source: window.location.pathname,
      planKey,
      previousPlanKey: selectedPlanKey,
      userId: user?.id || user?.sub || user?.userId,
      email: user?.email,
    });
    setSelectedPlanKey(planKey);
  }, [selectedPlanKey, user?.email, user?.id, user?.sub, user?.userId]);

  const handleSelfPacedClick = useCallback((ctaLocation) => {
    trackBootcampChoice({
      path: 'self_paced',
      ctaLocation,
      productSlug: 'ai-bootcamp-self-paced',
      planKey: 'self-paced-launch',
      destination: SELF_PACED_BOOTCAMP_PATH,
    });
  }, [trackBootcampChoice]);

  const handleFitCallClick = useCallback((ctaLocation) => {
    trackBootcampChoice({
      path: 'fit_call',
      ctaLocation,
      destination: BOOTCAMP_FIT_CALL_URL,
    });
  }, [trackBootcampChoice]);

  const renderBootcampDecisionCtas = (ctaPrefix) => (
    <div className="bootcamp-path-cta-row">
      <Button
        size="lg"
        className="bootcamp-cta-button"
        onClick={() => beginBootcampCheckout(`${ctaPrefix}_live_cta`)}
        disabled={getButtonState().disabled}
      >
        <FaGraduationCap className="me-2" />
        Join live cohort
      </Button>
      <Link
        to={SELF_PACED_BOOTCAMP_PATH}
        className="bootcamp-self-paced-btn-secondary"
        onClick={() => handleSelfPacedClick(`${ctaPrefix}_self_paced_cta`)}
      >
        <FaRocket className="me-2" />
        Start self-paced
      </Link>
      <a
        href={BOOTCAMP_FIT_CALL_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="bootcamp-book-call-btn-secondary"
        onClick={() => handleFitCallClick(`${ctaPrefix}_fit_call_cta`)}
      >
        <FaUsers className="me-2" />
        Book a fit call
      </a>
    </div>
  );

  // Enhanced price display with total charge information
  // AI Bootcamp is a standalone product with installment plans (not bundled with pro-plan)
  const getPriceBlurb = () => {
    if (!pricingInfo?.products) return 'Pricing Loading...';

    const bootcampDetails = getProductDisplayDetails('ai-bootcamp');

    if (bootcampDetails.currentFormatted === 'TBD') {
      return 'Pricing Loading...';
    }

    // AI Bootcamp is a single product - show its price directly
    // The backend will return the appropriate price based on the product's stripe price
    return bootcampDetails.currentFormatted;
  };

  // Get detailed pricing for display based on selected payment plan
  // AI Bootcamp pricing - single product with multiple payment plans from product_plan table
  const getPricingDetails = () => {
    // If we have payment plans from catalog, use those for accurate pricing
    if (paymentPlans.length > 0) {
      const selectedPlan = paymentPlans.find(p => p.planKey === selectedPlanKey) || paymentPlans[0];
      const upfrontPlan = paymentPlans.find(p => p.planKey === 'bootcamp-upfront');

      if (selectedPlan) {
        // amount = discounted price (if promotion), originalAmount = full price
        const amountInDollars = selectedPlan.amount ? (selectedPlan.amount / 100) : 0;
        const originalAmountInDollars = selectedPlan.originalAmount ? (selectedPlan.originalAmount / 100) : amountInDollars;
        const totalAmount = selectedPlan.totalAmount ? (selectedPlan.totalAmount / 100) : amountInDollars;
        const originalTotalAmount = selectedPlan.originalTotalAmount ? (selectedPlan.originalTotalAmount / 100) : totalAmount;
        const upfrontAmount = upfrontPlan?.amount ? (upfrontPlan.amount / 100) : totalAmount;
        const installments = selectedPlan.installments || 1;
        const hasDiscount = selectedPlan.hasDiscount || false;

        // Format price display
        const formatPrice = (amount) => `$${amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

        if (installments === 1) {
          // One-time payment
          return {
            totalInitialCharge: formatPrice(amountInDollars),
            monthlyCharge: null,
            monthlyAmount: null,
            isDiscounted: hasDiscount,
            originalTotal: hasDiscount ? formatPrice(originalAmountInDollars) : '',
            displayText: formatPrice(amountInDollars),
            installments: 1,
            isInstallmentPlan: false,
            appliedPromotion: selectedPlan.appliedPromotion
          };
        } else {
          // Installment plan - show per-payment amount
          return {
            totalInitialCharge: formatPrice(amountInDollars),
            monthlyCharge: `${installments} payments of ${formatPrice(amountInDollars)}`,
            monthlyAmount: `then ${installments - 1} more payments of ${formatPrice(amountInDollars)}`,
            isDiscounted: hasDiscount,
            originalTotal: hasDiscount ? formatPrice(originalAmountInDollars) : '',
            originalMonthlyCharge: hasDiscount ? `${installments} payments of ${formatPrice(originalAmountInDollars)}` : '',
            displayText: `${installments}x ${formatPrice(amountInDollars)}`,
            installments: installments,
            isInstallmentPlan: true,
            totalCost: formatPrice(totalAmount),
            originalTotalCost: hasDiscount ? formatPrice(originalTotalAmount) : '',
            savingsVsUpfront: upfrontAmount < totalAmount ? null : formatPrice(totalAmount - upfrontAmount),
            appliedPromotion: selectedPlan.appliedPromotion
          };
        }
      }
    }

    // Fallback to legacy pricing info
    if (!pricingInfo?.products) {
      return {
        totalInitialCharge: '$0.00',
        monthlyCharge: null,
        isDiscounted: false,
        originalTotal: '$0.00',
        displayText: 'Pricing Loading...'
      };
    }

    const bootcampDetails = getProductDisplayDetails('ai-bootcamp');

    // AI Bootcamp is NOT a recurring subscription - it's a one-time or installment purchase
    // The price shown is the full bootcamp price (from database)
    return {
      totalInitialCharge: bootcampDetails.currentFormatted,
      monthlyCharge: null, // Not a recurring product
      isDiscounted: bootcampDetails.isDiscounted,
      originalTotal: bootcampDetails.isDiscounted ? bootcampDetails.originalFormatted : '',
      displayText: getPriceBlurb(),
      upfrontAmount: bootcampDetails.currentFormatted,
      monthlyAmount: null // Not a recurring product
    };
  };

  // Enhanced button state logic - NEVER show loading in button text
  const getButtonState = () => {
    const isDisabled = isLoadingPricing; // Only disable if pricing is still loading
    const buttonText = 'Join the AI Bootcamp'; // Always show compelling action text

    return {
      disabled: isDisabled,
      text: buttonText
    };
  };

  // Bootcamp-specific FAQs
const bootcampFaqs = [
    {
      id: 'bootcamp-requirements',
      question: 'Do I need coding experience to join the AI Bootcamp?',
      answer: 'No coding experience is required. The Bootcamp shows you how to use plain-English prompts and BotSpot workflows to move from idea to testable strategy logic.'
    },
    {
      id: 'bootcamp-schedule',
      question: 'What if I miss a live session?',
      answer: 'Sessions are recorded and added to the Bootcamp portal after class. You can catch up on prior sessions before the next live class.'
    },
    {
      id: 'bootcamp-access',
      question: 'How long do I have access to the bootcamp content?',
      answer: 'Your Bootcamp purchase includes replay access through the course portal. Self-paced and live buyers both use the portal, while live buyers also get cohort registration and join links.'
    },
    {
      id: 'bootcamp-support',
      question: 'What kind of support do I get during the bootcamp?',
      answer: 'Live cohort buyers get direct help during class and Q&A. Self-paced buyers can start with the recordings now and apply what they paid toward a future live seat.'
    },
    {
      id: 'bootcamp-deployment',
      question: 'Can I deploy bots immediately after the bootcamp?',
      answer: 'The course is built around practical implementation inside BotSpot: define the idea, create the workflow, backtest it, refine it, and learn what needs to be true before anything goes live.'
    }
  ];

  // Bootcamp features highlighting key value propositions
  const bootcampFeatures = [
    {
      icon: <FaGraduationCap />,
      title: 'AI Bootcamp Access',
      description: 'Five-week implementation path with live cohort access or a self-paced replay option'
    },
    {
      icon: <FaRocket />,
      title: 'Build Inside BotSpot',
      description: 'Turn ideas into strategy logic, backtests, revisions, and practical next steps'
    },
    {
      icon: <FaStar />,
      title: 'Templates and Examples',
      description: 'Use BotSpot examples and course workflows as starting points while you learn'
    }
  ];

  if (isCourseLoading) {
    return (
      <div className="ai-bootcamp-sales-loading">
        <Container>
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading bootcamp information...</p>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>AI Bootcamp — Build AI Investing Workflows | BotSpot</title>
        <meta name="description" content="Build an AI investing workflow inside BotSpot. Choose the self-paced Bootcamp or join the next live cohort with Q&A, replays, and payment plans." />
        <link rel="canonical" href="https://botspot.trade/ai-bot-builder-bootcamp" />

        {/* Open Graph */}
        <meta property="og:title" content="AI Bootcamp | BotSpot" />
        <meta property="og:description" content="Start self-paced today or join the next live cohort to build AI investing workflows inside BotSpot." />
        <meta property="og:image" content="https://botspot.trade/social-share-image.jpg" />
        <meta property="og:url" content="https://botspot.trade/ai-bot-builder-bootcamp" />
        <meta property="og:type" content="website" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="AI Bootcamp | BotSpot" />
        <meta name="twitter:description" content="Build AI investing workflows inside BotSpot with self-paced or live Bootcamp options." />
        <meta name="twitter:image" content="https://botspot.trade/social-share-image.jpg" />
      </Helmet>
      <div className="ai-bootcamp-sales-page" data-testid="sales-page">
        {/* Progress Indicator */}
        <div className="bootcamp-progress-indicator">
          <div className="progress-bar" style={{ width: `${scrollProgress}%` }}></div>
        </div>

      {/* CONVERSION-OPTIMIZED HERO - Mobile-first, CTAs above fold */}
      <div className="bootcamp-sales-hero">
        {/* Floating elements - desktop only for visual flair */}
        <div className="bootcamp-hero-floating-elements desktop-only">
          <FaRobot className="floating-bot" />
          <FaChartLine className="floating-bot" />
        </div>

        <Container>
          <Row className="justify-content-center">
            <Col lg={10} xl={9}>
              <div className="bootcamp-sales-hero-content">

                {/* HERO HEADLINE - Compelling transformation message */}
                <div className="bootcamp-hero-headline">
                  <h1 className="bootcamp-main-headline">
                    Build Your AI Investing Team
                  </h1>
                  <p className="bootcamp-sub-headline">
                    Start self-paced today or join the next live cohort for direct help and Q&A
                  </p>
                </div>

                {/* PRIMARY PATH CHOICE - MUST be above fold on mobile */}
                <div className="bootcamp-sales-cta-section">
                  <div className="bootcamp-path-options" aria-label="Choose how to take the AI Bootcamp">
                    <button
                      type="button"
                      className="bootcamp-path-card bootcamp-path-card--live"
                      onClick={() => beginBootcampCheckout('hero_live_path_card')}
                      disabled={getButtonState().disabled}
                    >
                      <span className="bootcamp-path-kicker">Live Bootcamp</span>
                      <strong>Join the next live cohort</strong>
                      <span>5 live Wednesday sessions, Q&A, replays, and payment plans.</span>
                      <em>Live help, feedback, and accountability</em>
                    </button>

                    <Link
                      className="bootcamp-path-card bootcamp-path-card--self-paced"
                      to={SELF_PACED_BOOTCAMP_PATH}
                      onClick={() => handleSelfPacedClick('hero_self_paced_path_card')}
                    >
                      <span className="bootcamp-path-kicker">Self-paced</span>
                      <strong>Start the course today for $499</strong>
                      <span>Recorded lessons, resources, 30 days of Pro, and a live upgrade path.</span>
                      <em>$499 launch price, normally $997</em>
                    </Link>
                  </div>

                  <div className="bootcamp-dual-cta bootcamp-dual-cta--secondary">
                    <a
                      href={BOOTCAMP_FIT_CALL_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bootcamp-book-call-btn"
                      onClick={() => handleFitCallClick('hero_secondary_cta')}
                    >
                      <FaUsers className="me-2" />
                      Book a fit call
                    </a>
                  </div>

                  {/* Micro social proof + guarantee */}
                  <div className="bootcamp-hero-social-proof">
                    <span className="hero-stars">★★★★★</span>
                    <span className="hero-stat-inline">2,400+ traders</span>
                    <span className="hero-divider">•</span>
                    <span className="hero-stat-inline">100% no-code</span>
                  </div>

                  <p className="bootcamp-sales-cta-subtext">
                    <span className="guarantee-text">🔒 30-day money-back guarantee</span>
                    <span className="cancel-anytime-text">Payment plans available</span>
                  </p>

                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Bootcamp Content using individual components for precise control */}
      <div className="bootcamp-sales-showcase">
        <BootcampHero onRegisterClick={() => beginBootcampCheckout('showcase_hero_cta')} />

        {/* Testimonials Section */}
        <div className="bootcamp-testimonials-section">
          <Container>
            <h2 className="bootcamp-overview-title" style={{ marginTop: '4rem' }}>
              What Our Students Say
            </h2>
            <Suspense fallback={<div className="text-center"><Spinner animation="border" /> Loading Testimonials...</div>}>
              <TestimonialSlider testimonials={bootcampTestimonials} />
            </Suspense>
          </Container>
        </div>

        <BootcampOverview nextSessionDate={liveBootcampSessionDate} />

        {/* CTA after Overview */}
        <div className="bootcamp-cta-section">
          <Container>
            <div className="text-center py-4">
              <div className="bootcamp-dual-cta-row">
                {renderBootcampDecisionCtas('overview')}
              </div>
              <p className="bootcamp-cta-subtext mt-2">
                Choose live if you want direct help, or self-paced if you want to start now.
              </p>
            </div>
          </Container>
        </div>

        <div className="bootcamp-self-paced-link-band bootcamp-self-paced-link-band--prominent">
          <Container>
            <div className="bootcamp-self-paced-link-inner">
              <img
                src="/images/revenue/self-paced-bootcamp-archive.webp"
                alt="Self-paced AI Bootcamp course workspace"
                width="1024"
                height="572"
                loading="lazy"
              />
              <div>
                <p className="bootcamp-self-paced-eyebrow">Self-paced option</p>
                <h3>Want the course without live classes?</h3>
                <p>The self-paced Bootcamp teaches the same core workflow: define an investing idea, turn it into BotSpot logic, test it, improve it, and understand what to build next.</p>
                <p>It is the lower-cost path for people who want to learn now and upgrade to a live cohort later if they want direct help.</p>
                <Link
                  to={SELF_PACED_BOOTCAMP_PATH}
                  className="bootcamp-self-paced-link"
                  onClick={() => handleSelfPacedClick('pre_pricing_self_paced_band')}
                >
                  View the self-paced Bootcamp
                </Link>
              </div>
            </div>
          </Container>
        </div>

        {/* First Pricing Section - Live cohort pricing */}
        <BootcampPricingSection
          title="Live Bootcamp Pricing"
          saleEndTime={saleEndTime}
          isLoadingPricing={isLoadingPricing}
          getPricingDetails={getPricingDetails}
          pricingError={pricingError}
          setSaleEndTime={setSaleEndTime}
          className="bootcamp-pricing-first"
          onCtaClick={handleProCtaClick}
          ctaText="Join the AI Bootcamp"
          showCta={true}
          paymentPlans={paymentPlans}
          selectedPlanKey={selectedPlanKey}
          onPlanSelect={handlePlanSelect}
        />

        <div className="bootcamp-self-paced-link-band">
          <Container>
            <div className="bootcamp-self-paced-link-inner bootcamp-self-paced-link-inner--text-only">
              <div>
                <h3>Self-paced is a separate lower-cost option.</h3>
                <p>Use self-paced when you want the course now. Use live when you want direct help, Q&A, and implementation time.</p>
                <Link
                  to={SELF_PACED_BOOTCAMP_PATH}
                  className="bootcamp-self-paced-link"
                  onClick={() => handleSelfPacedClick('pricing_self_paced_band')}
                >
                  View the self-paced Bootcamp
                </Link>
              </div>
            </div>
          </Container>
        </div>

        <BootcampBenefits />

        {/* CTA after Benefits */}
        <div className="bootcamp-cta-section">
          <Container>
            <div className="text-center py-4">
              <div className="bootcamp-dual-cta-row">
                {renderBootcampDecisionCtas('benefits')}
              </div>
              <p className="bootcamp-cta-subtext mt-2">
                Self-paced is lower cost. Live adds Q&A, feedback, and implementation time.
              </p>
            </div>
          </Container>
        </div>

        <BootcampCurriculum
          bootcampData={bootcampData}
          nextSessionDate={liveBootcampSessionDate}
          getWeekDate={getWeekDate}
        />

        {/* Final CTA after Curriculum */}
        <div className="bootcamp-cta-section">
          <Container>
            <div className="text-center py-4">
              <div className="bootcamp-dual-cta-row">
                {renderBootcampDecisionCtas('curriculum')}
              </div>
              <p className="bootcamp-cta-subtext mt-2">
                Learn the workflow, test ideas, and build inside BotSpot
              </p>
            </div>
          </Container>
        </div>
      </div>

      {/* Bootcamp Features Section */}
      <div className="bootcamp-sales-features">
        <Container>
          <div className="text-center mb-5">
            <h2 className="bootcamp-sales-section-title">
              Everything You Get with <span className="bootcamp-sales-highlight">AI Bootcamp</span>
            </h2>
            <p className="bootcamp-sales-section-subtitle">
              A practical BotSpot training path with live and self-paced options
            </p>
          </div>

          {/* Bootcamp Features Layout - Horizontal cards with icon on left */}
          <Row className="g-4 justify-content-center">
            {bootcampFeatures.map((feature, index) => (
              <Col key={index} lg={4} md={6} sm={12}>
                <div className="bootcamp-pro-feature-card h-100">
                  <div className="bootcamp-pro-feature-icon">
                    {feature.icon}
                  </div>
                  <div className="bootcamp-pro-feature-content">
                    <h3 className="bootcamp-pro-feature-title">
                      {feature.title}
                    </h3>
                    <p className="bootcamp-pro-feature-description">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </div>

      {/* Second Pricing Section - After showcasing value */}
      <BootcampPricingSection
        title="Live Bootcamp Pricing"
        saleEndTime={saleEndTime}
        isLoadingPricing={isLoadingPricing}
        getPricingDetails={getPricingDetails}
        pricingError={pricingError}
        setSaleEndTime={setSaleEndTime}
        className="bootcamp-pricing-second"
        onCtaClick={handleProCtaClick}
        ctaText="Get Started Now"
        showCta={true}
        paymentPlans={paymentPlans}
        selectedPlanKey={selectedPlanKey}
        onPlanSelect={handlePlanSelect}
      />

      {/* FAQ Section */}
      <div className="bootcamp-sales-faq">
        <Container>
          <div className="text-center mb-5">
            <h2 className="bootcamp-sales-section-title">
              Frequently Asked <span className="bootcamp-sales-highlight">Questions</span>
            </h2>
          </div>
          <FAQSection
            faqs={bootcampFaqs}
            faqType="bootcamp"
          />
        </Container>
      </div>

      {/* Final CTA */}
      <div className="bootcamp-sales-final-cta">
        <Container>
          <Row className="justify-content-center">
            <Col lg={10} xl={8}>
              <div className="pro-cta-content text-center">
                <h2 className="pro-cta-title">
                  Ready to build your AI investing workflow?
                </h2>
                <p className="pro-cta-subtitle">
                  Choose self-paced if you want to start now, or join the live cohort if you want direct help.
                </p>

                {renderBootcampDecisionCtas('final')}

                <div className="mt-4">
                  <div className="d-flex align-items-center justify-content-center">
                    <FaStar className="text-warning me-2" />
                    <small className="text-muted">30-day money-back guarantee</small>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
      </div>
    </>
  );
};

export default AIBootcampSalesPage;
